/**
 * PinnedReportsSection — dashboard tile grid for AI Report Builder pins.
 *
 * Mounted on the home (Mission Control) and LEI Dashboard pages.
 * For each pin we hit ``GET /api/mc/reports/pins/list?surface=...``;
 * the backend re-executes the pinned spec against live data and
 * returns the rendered ``blocks[]`` ready for direct charting.
 *
 * Each tile shows:
 *   • a small caption with the source report name + "Open in builder"
 *   • the live chart (bar/line/big-number/table/...) via the same
 *     renderers used inside the Report Builder
 *   • an unpin (×) action on hover
 *
 * Renders nothing when the caller has no pins on this surface — we
 * deliberately do NOT show an empty CTA box to keep dashboards
 * uncluttered for users who haven't adopted pinning yet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pin, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { ReportBlockTile } from './ReportBlockTile';

export default function PinnedReportsSection({ surface, title = 'Pinned reports' }) {
  const navigate = useNavigate();
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPins = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/mc/reports/pins/list?surface=${encodeURIComponent(surface)}`);
      setPins(data?.pins || []);
    } catch (e) {
      // Pins are best-effort decoration — don't poison the dashboard
      // with a toast when listing fails (e.g. user lost admin rights).
      // eslint-disable-next-line no-console
      console.warn('[pinned-reports] list failed', e?.response?.status);
      setPins([]);
    } finally {
      setLoading(false);
    }
  }, [surface]);

  useEffect(() => { fetchPins(); }, [fetchPins]);

  const onUnpin = async (pinId) => {
    try {
      await api.delete(`/mc/reports/pins/${pinId}`);
      setPins((ps) => ps.filter((p) => p.id !== pinId));
      toast.success('Unpinned');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not unpin');
    }
  };

  // Hide section entirely when nothing to show — keeps the dashboard
  // uncluttered for users who haven't adopted pinning yet.
  if (!loading && !pins.length) return null;

  return (
    <section className="space-y-3" data-testid={`pinned-reports-${surface}`}>
      <div className="flex items-center gap-2">
        <Pin className="h-4 w-4 text-[#394fd4]" />
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        <span className="text-xs text-slate-500">— live data, refreshed on load</span>
      </div>
      {loading ? (
        <div className="text-xs text-slate-500 italic px-1">Loading pinned reports…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pins.map((p) => (
            <article
              key={p.id}
              className="rounded-xl border border-slate-200 bg-white p-4 space-y-3 hover:border-[#394fd4]/40 transition-colors"
              data-testid={`pin-tile-${p.id}`}
            >
              {/* Caption row */}
              <header className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-700 line-clamp-1">
                    {p.report_title || p.report_name}
                  </div>
                  {p.report_headline ? (
                    <div className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">
                      {p.report_headline}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/reports/builder/${p.source_report_id}`)}
                    className="p-1 rounded text-slate-400 hover:text-[#394fd4] hover:bg-slate-50"
                    aria-label="Open in builder"
                    data-testid={`pin-open-${p.id}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onUnpin(p.id)}
                    className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                    aria-label="Unpin"
                    data-testid={`pin-unpin-${p.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </header>
              {/* Blocks */}
              <div className="space-y-3">
                {(p.blocks || []).map((b) => (
                  <ReportBlockTile
                    key={b.id}
                    block={b}
                    onAnnotationClick={(prompt) => {
                      // From a pinned tile, an annotation chip opens
                      // the source report in the builder with the
                      // suggested prompt pre-filled. The builder picks
                      // it up via the URL and continues the conversation.
                      navigate(
                        `/admin/reports/builder/${p.source_report_id}?prompt=${encodeURIComponent(prompt)}`,
                      );
                    }}
                  />
                ))}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
